// filepath: /Users/evan/Documents/gnoknews/src/app/services/translate.service.ts
import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { AuthService } from './auth.service';
import { User } from '../entities/user.model';
import { catchError, map, Observable } from 'rxjs';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { WordCard } from '../entities/wordcard.model';
import { ActivatedRoute, Router } from '@angular/router';

@Injectable({
  providedIn: 'root',
})
export class AppTranslateService {
  private backendUrl: string;
  private translateArticleEndpoint: string;
  private createWordCardEndpoint: string;
  public nativeLanguage: string = 'en';
  public setFromURL: boolean = false;
  private supportedLangs: string[] = ['en', 'de', 'fr', 'hi', 'ja', 'pt', 'ru', 'zh', 'ar', 'es', 'ur', 'bn', 'he'];

  constructor(private translate: TranslateService, 
    private authService: AuthService, 
    private http: HttpClient,
    private router: Router) {
        // Set the translation endpoint based on the environment
        if (environment.production) {
          this.backendUrl = 'https://us-central1-gno-knews.cloudfunctions.net';
        } else if (!environment.useEmulators) {
          this.backendUrl = 'https://us-central1-gno-knews-staging.cloudfunctions.net';
        } else {
          this.backendUrl = 'http://localhost:5000';
        }
        // Set the translation endpoint for article translation
        this.translateArticleEndpoint = `${this.backendUrl}/translate_article`;
        // Set the create_word_card endpoint
        this.createWordCardEndpoint = `${this.backendUrl}/create_word_card`;
        
        // CRITICAL: Set default language to prevent placeholder text
        this.translate.setDefaultLang('en');
    }

  initializeTranslation(route: ActivatedRoute) {
    route.queryParams.subscribe(params => {
        const lang = params['lang'];
        console.log('Language param from URL:', lang);
        if (lang && this.supportedLangs.includes(lang)) {
          this.nativeLanguage = lang;
          this.translate.use(this.nativeLanguage);
          this.setFromURL = true;
          return;
        }
    });
    this.authService.currentUser$.subscribe((user: User | null) => {
      // Only set language if it hasn't been explicitly set already
      if (this.translate.currentLang && this.supportedLangs.includes(this.translate.currentLang) && !this.setFromURL) {
        // Language is already set and valid, don't override it
        this.nativeLanguage = this.translate.currentLang;
        console.log('Using already set language:', this.nativeLanguage);
        return;
      }
      
      if (user && user.nativeLanguage && !this.setFromURL) {
        this.nativeLanguage = user.nativeLanguage;
      }
      else {
        if (this.translate.getBrowserLang() && !this.setFromURL) {
            let browserLang = this.translate.getBrowserLang() as string;
            this.nativeLanguage = browserLang;
            console.log('Using browser language:', this.nativeLanguage);
        }
      }
      if (!this.supportedLangs.includes(this.nativeLanguage)) {
          console.log(`Language ${this.nativeLanguage} is not supported. Defaulting to English.`);
          this.nativeLanguage = 'en';
      }
      if (!this.setFromURL) {
        this.translate.use(this.nativeLanguage);
      }
    });
  }

  wakeUpEndpoints() {
    // Send dummy requests to warm up both endpoints
    this.http.post(this.translateArticleEndpoint, { ping: true }).subscribe({
      next: (response) => {
        console.log('translateArticleEndpoint responded with 200', response);
      },
      error: (error) => {
        console.log('translateArticleEndpoint did not respond with 200:', error.status);
      }
    });
    this.http.post(this.createWordCardEndpoint, { ping: true }).subscribe({
      next: (response) => {
        console.log('createWordCardEndpoint responded with 200', response);
      },
      error: (error) => {
        console.log('createWordCardEndpoint did not respond with 200:', error.status);
      }
    });
  }

  translateArticle(articleId: string, targetLanguage: string): Observable<any> {
    const data = {
      article_id: articleId,
      target_language: targetLanguage,
    };

    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });

    return this.http.post(this.translateArticleEndpoint, data, { headers });
  }

  getWordCard(word: string, contextSentence: string, native_language: string, target_language: string, proficiency_level: number): Observable<WordCard> {
    const data = {
      word: word,
      context_sentence: contextSentence,
      native_language: native_language,
      target_language: target_language,
      proficiency_level: proficiency_level,
    };

    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });

    return this.http.post(this.createWordCardEndpoint, data, { headers }).pipe(
      map((response: any) => {
        const wordCard: WordCard = response.word_card as WordCard;
        return wordCard;
      }),
      catchError((error) => {
        console.error('Error fetching word card:', error);
        throw new Error('Failed to fetch word card');
      })
    );
  }
}